// Logger utility that allows for channel-based logging with configurable visibility
import { datadogLogs } from '@datadog/browser-logs';
import pino from 'pino';

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4, // Used to completely disable a channel
}

// Format for timestamp output
export enum TimestampFormat {
  NONE = 'none', // No timestamp
  SHORT = 'short', // HH:MM:SS
  FULL = 'full', // HH:MM:SS.mmm
  ISO = 'iso', // ISO format (YYYY-MM-DDTHH:MM:SS.mmmZ)
  RELATIVE = 'relative', // Milliseconds since page load
}

interface LoggerOptions {
  level?: LogLevel;
  enabled?: boolean;
  timestampFormat?: TimestampFormat;
  includeChannel?: boolean; // Whether to include the channel name in the output
}

/**
 * Interface for a logger instance
 */
export interface Logger {
  debug(message: string, ...args: any[]): void;
  info(message: string, ...args: any[]): void;
  warn(message: string, ...args: any[]): void;
  error(message: string, ...args: any[]): void;

  // Enable/disable this specific logger
  setEnabled(enabled: boolean): void;

  // Set the minimum log level for this logger
  setLevel(level: LogLevel): void;

  // Set the timestamp format
  setTimestampFormat(format: TimestampFormat): void;

  // Set whether to include the channel name
  setIncludeChannel(include: boolean): void;
}

/**
 * Class that implements the Logger interface
 */
class LoggerImpl implements Logger {
  private channel: string;
  private enabled: boolean;
  private level: LogLevel;
  private timestampFormat: TimestampFormat;
  private includeChannel: boolean;
  private pageLoadTime: number = performance.now();

  constructor(channel: string, options: LoggerOptions = {}) {
    this.channel = channel;
    this.enabled = options.enabled ?? true;
    this.level = options.level ?? LogLevel.INFO;
    this.timestampFormat = options.timestampFormat ?? TimestampFormat.NONE; // No timestamp by default
    this.includeChannel = options.includeChannel ?? true;
  }

  debug(message: string, ...args: any[]): void {
    this.log(LogLevel.DEBUG, message, ...args);
  }

  info(message: string, ...args: any[]): void {
    this.log(LogLevel.INFO, message, ...args);
  }

  warn(message: string, ...args: any[]): void {
    this.log(LogLevel.WARN, message, ...args);
  }

  error(message: string, ...args: any[]): void {
    this.log(LogLevel.ERROR, message, ...args);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  setLevel(level: LogLevel): void {
    this.level = level;
  }

  setTimestampFormat(format: TimestampFormat): void {
    this.timestampFormat = format;
  }

  setIncludeChannel(include: boolean): void {
    this.includeChannel = include;
  }

  private log(level: LogLevel, message: string, ...args: any[]): void {
    if (!this.enabled || level < this.level) {
      return;
    }

    let prefix = '';

    // Add timestamp according to format
    if (this.timestampFormat !== TimestampFormat.NONE) {
      const timestamp = this.formatTimestamp();
      prefix += `[${timestamp}]`;
    }

    // Add channel name if enabled
    if (this.includeChannel) {
      prefix += `[${this.channel}]`;
    }

    // Add separator if we have a prefix
    if (prefix) {
      prefix += ' - ';
    }

    // Get the pino logger instance from LogManager
    const pinoLogger = LogManager.getPinoLogger();

    // Combine prefix with message for final output
    const finalMessage = prefix + message;

    if (pinoLogger) {
      // Use pino logger with our custom formatting
      switch (level) {
        case LogLevel.DEBUG:
          pinoLogger.debug({ args }, finalMessage);
          break;
        case LogLevel.INFO:
          pinoLogger.info({ args }, finalMessage);
          break;
        case LogLevel.WARN:
          pinoLogger.warn({ args }, finalMessage);
          break;
        case LogLevel.ERROR:
          pinoLogger.error({ args }, finalMessage);
          break;
      }
    } else {
      // Fallback to console if pino is not available
      switch (level) {
        case LogLevel.DEBUG:
          console.debug(finalMessage, ...args);
          break;
        case LogLevel.INFO:
          console.info(finalMessage, ...args);
          break;
        case LogLevel.WARN:
          console.warn(finalMessage, ...args);
          break;
        case LogLevel.ERROR:
          console.error(finalMessage, ...args);
          break;
      }
    }
  }

  /**
   * Format timestamp according to the selected format
   */
  private formatTimestamp(): string {
    const now = new Date();

    switch (this.timestampFormat) {
      case TimestampFormat.SHORT:
        return [
          now.getHours().toString().padStart(2, '0'),
          now.getMinutes().toString().padStart(2, '0'),
          now.getSeconds().toString().padStart(2, '0'),
        ].join(':');

      case TimestampFormat.FULL:
        return (
          [
            now.getHours().toString().padStart(2, '0'),
            now.getMinutes().toString().padStart(2, '0'),
            now.getSeconds().toString().padStart(2, '0'),
          ].join(':') +
          '.' +
          now.getMilliseconds().toString().padStart(3, '0')
        );

      case TimestampFormat.ISO:
        return now.toISOString();

      case TimestampFormat.RELATIVE:
        return `+${(performance.now() - this.pageLoadTime).toFixed(0)}ms`;

      default:
        return '';
    }
  }
}

/**
 * Central manager for all loggers
 */
/**
 * Handlers backing Log.startTimer/endTimer. Registered by the analytics
 * layer at startup (AnalyticsStore) so the Logger can offer transaction
 * timing app-wide without importing monitoring code (keeps layering clean).
 */
export interface TimerHandlers {
  start(key: string, metadata?: Record<string, unknown>): void;
  mark(key: string, markName: string): void;
  end(key: string, metadata?: Record<string, unknown>): void;
  cancel(key: string, reason?: string): void;
}

export class LogManager {
  private static loggers: Map<string, Logger> = new Map();
  private static timerHandlers: TimerHandlers | null = null;

  /** Called once by the analytics layer to back the timer API. */
  static registerTimerHandlers(handlers: TimerHandlers): void {
    this.timerHandlers = handlers;
  }

  /**
   * PLAT-11041/11042 — time actual transactions:
   * Log.startTimer(KEY) ... Log.endTimer(KEY) emits one entry with the true
   * elapsed duration (plus any marks recorded along the way).
   */
  static startTimer(key: string, metadata?: Record<string, unknown>): void {
    if (!this.timerHandlers) {
      this.getLogger('LogManager').warn(
        `Log.startTimer('${key}') before analytics init — dropped`,
      );
      return;
    }
    this.timerHandlers.start(key, metadata);
  }

  /** Record an intermediate gate on a running timer. */
  static markTimer(key: string, markName: string): void {
    this.timerHandlers?.mark(key, markName);
  }

  /** End a running timer and emit its entry. No-op if not running. */
  static endTimer(key: string, metadata?: Record<string, unknown>): void {
    this.timerHandlers?.end(key, metadata);
  }

  /** Cancel a running timer (user gave up / surface closed). */
  static cancelTimer(key: string, reason?: string): void {
    this.timerHandlers?.cancel(key, reason);
  }
  private static globalLevel: LogLevel = LogLevel.INFO;
  private static globalEnabled: boolean = true;
  private static globalTimestampFormat: TimestampFormat = TimestampFormat.NONE; // No timestamp by default
  private static globalIncludeChannel: boolean = true;
  private static initialized: boolean = false;
  private static pinoLogger: pino.Logger | null = null;

  /**
   * Initialize the LogManager with environment variables and defaults
   * This should be called early in the application startup
   */
  static initialize(): void {
    if (this.initialized) return;

    this.initialized = true;

    // Initialize pino logger
    this.initializePinoLogger();

    // Check for environment variables
    this.initFromEnvironment();

    // Set up browser console commands if in browser environment
    this.setupBrowserCommands();
  }

  /**
   * Initialize the pino logger with browser and Node.js compatibility
   */
  private static initializePinoLogger(): void {
    try {
      // Detect if we're in a browser environment
      const isBrowser = typeof window !== 'undefined';

      if (isBrowser) {
        // Browser configuration - use custom write functions to preserve formatting
        this.pinoLogger = pino({
          browser: {
            write: {
              debug: (o: any) => this.customWrite('debug', o),
              info: (o: any) => this.customWrite('info', o),
              warn: (o: any) => this.customWrite('warn', o),
              error: (o: any) => this.customWrite('error', o),
            },
          },
          level: 'trace', // Allow all levels, filtering handled by our logic
        });
      } else {
        // Node.js configuration - disable pino and use console directly
        // This ensures compatibility with existing behavior and tests
        this.pinoLogger = null;
      }
    } catch (error) {
      // Fallback to console if pino initialization fails
      console.warn(
        'Failed to initialize pino logger, falling back to console:',
        error,
      );
      this.pinoLogger = null;
    }
  }

  /**
   * Custom write function for browser environment to preserve existing formatting
   */
  private static customWrite(level: string, logObject: any): void {
    // Extract the message and any additional data from pino's log object
    const message = logObject.msg || '';
    const args = logObject.args || [];

    // Use the original console methods to maintain browser devtools formatting
    switch (level) {
      case 'debug':
        console.debug(message, ...args);
        break;
      case 'info':
        console.info(message, ...args);
        break;
      case 'warn':
        console.warn(message, ...args);
        break;
      case 'error':
        console.error(message, ...args);
        break;
    }

    this.sendToDataDog(level, message, args);
  }

  /**
   * Send log to DataDog with appropriate level mapping
   */
  private static sendToDataDog(
    level: string,
    message: string,
    args: any[],
  ): void {
    try {
      // Create context object from args if they exist
      const context = args.length > 0 ? { args } : {};

      // Map levels and send to DataDog
      switch (level) {
        case 'debug':
          datadogLogs.logger.debug(message, context);
          break;
        case 'info':
          datadogLogs.logger.info(message, context);
          break;
        case 'warn':
          datadogLogs.logger.warn(message, context);
          break;
        case 'error':
          datadogLogs.logger.error(message, context);
          break;
      }
    } catch (error) {
      // Silently fail to avoid breaking logging if DataDog has issues
      console.warn('Failed to send log to DataDog:', error);
    }
  }

  /**
   * Initialize logger settings from environment variables
   */
  private static initFromEnvironment(): void {
    try {
      // Check for NODE_ENV or process.env in Node.js environments
      if (typeof process !== 'undefined' && process.env) {
        // Global log level
        const envLogLevel = process.env.LOG_LEVEL;
        if (envLogLevel) {
          this.setGlobalLevelFromString(envLogLevel);
        }

        // Enable/disable logging entirely
        const envLoggingEnabled = process.env.LOGGING_ENABLED;
        if (envLoggingEnabled !== undefined) {
          this.setGlobalEnabled(envLoggingEnabled.toLowerCase() === 'true');
        }

        // Enable specific channels (comma-separated list)
        const enabledChannels = process.env.LOG_ENABLED_CHANNELS;
        if (enabledChannels) {
          const channels = enabledChannels.split(',');
          channels.forEach(channel => {
            this.setChannelEnabled(channel.trim(), true);
          });
        }

        // Disable specific channels (comma-separated list)
        const disabledChannels = process.env.LOG_DISABLED_CHANNELS;
        if (disabledChannels) {
          const channels = disabledChannels.split(',');
          channels.forEach(channel => {
            this.setChannelEnabled(channel.trim(), false);
          });
        }
      }
    } catch (e) {
      // Silently fail if environment variable access fails
      console.warn(
        'Failed to initialize logger from environment variables:',
        e,
      );
    }
  }

  /**
   * Set up browser console commands for runtime configuration
   */
  private static setupBrowserCommands(): void {
    try {
      // Only set up in browser environment and not in test environment
      const isTest =
        typeof process !== 'undefined' && process.env.NODE_ENV === 'test';
      if (typeof window !== 'undefined' && !isTest) {
        // Add to window object for console access
        (window as any).__logger = {
          enableChannel: (channel: string) => {
            this.setChannelEnabled(channel, true);
            console.log(`Enabled logging for channel: ${channel}`);
          },
          disableChannel: (channel: string) => {
            this.setChannelEnabled(channel, false);
            console.log(`Disabled logging for channel: ${channel}`);
          },
          setGlobalLevel: (level: string) => {
            const success = this.setGlobalLevelFromString(level);
            if (success) {
              console.log(`Set global log level to: ${level}`);
            } else {
              console.error(
                `Invalid log level: ${level}. Valid levels are: debug, info, warn, error, none`,
              );
            }
          },
          listChannels: () => {
            const channels = Array.from(this.loggers.keys());
            console.log('Available logging channels:', channels);
            return channels;
          },
          enableAll: () => {
            this.setGlobalEnabled(true);
            console.log('Enabled all logging channels');
          },
          disableAll: () => {
            this.setGlobalEnabled(false);
            console.log('Disabled all logging channels');
          },
        };

        console.info(
          'Logger commands available via window.__logger in the browser console',
        );
      }
    } catch (e) {
      // Silently fail if browser detection fails
      console.warn('Failed to set up browser logger commands:', e);
    }
  }

  /**
   * Helper to set global level from string value
   */
  private static setGlobalLevelFromString(level: string): boolean {
    switch (level.toLowerCase()) {
      case 'debug':
        this.setGlobalLevel(LogLevel.DEBUG);
        return true;
      case 'info':
        this.setGlobalLevel(LogLevel.INFO);
        return true;
      case 'warn':
        this.setGlobalLevel(LogLevel.WARN);
        return true;
      case 'error':
        this.setGlobalLevel(LogLevel.ERROR);
        return true;
      case 'none':
        this.setGlobalLevel(LogLevel.NONE);
        return true;
      default:
        return false;
    }
  }

  /**
   * Get a logger for a specific channel
   * @param channel The channel name for this logger
   * @param options Optional configuration options for this logger
   * @returns A logger instance
   */
  static getLogger(
    channel: string,
    options: Partial<LoggerOptions> = {},
  ): Logger {
    // Ensure logger is initialized
    if (!this.initialized) {
      this.initialize();
    }

    if (!this.loggers.has(channel)) {
      const logger = new LoggerImpl(channel, {
        level: options.level !== undefined ? options.level : this.globalLevel,
        enabled:
          options.enabled !== undefined ? options.enabled : this.globalEnabled,
        timestampFormat:
          options.timestampFormat !== undefined
            ? options.timestampFormat
            : this.globalTimestampFormat,
        includeChannel:
          options.includeChannel !== undefined
            ? options.includeChannel
            : this.globalIncludeChannel,
      });
      this.loggers.set(channel, logger);
    } else if (Object.keys(options).length > 0) {
      // If the logger already exists and options are provided, update its configuration
      const logger = this.loggers.get(channel)!;
      if (options.level !== undefined) {
        logger.setLevel(options.level);
      }
      if (options.enabled !== undefined) {
        logger.setEnabled(options.enabled);
      }
      if (options.timestampFormat !== undefined) {
        logger.setTimestampFormat(options.timestampFormat);
      }
      if (options.includeChannel !== undefined) {
        logger.setIncludeChannel(options.includeChannel);
      }
    }

    return this.loggers.get(channel)!;
  }

  /**
   * Enable or disable a specific channel
   * @param channel The channel to configure
   * @param enabled Whether the channel should be enabled
   */
  static setChannelEnabled(channel: string, enabled: boolean): void {
    if (this.loggers.has(channel)) {
      this.loggers.get(channel)!.setEnabled(enabled);
    }
  }

  /**
   * Set the log level for a specific channel
   * @param channel The channel to configure
   * @param level The minimum log level to display
   */
  static setChannelLevel(channel: string, level: LogLevel): void {
    if (this.loggers.has(channel)) {
      this.loggers.get(channel)!.setLevel(level);
    }
  }

  /**
   * Enable or disable all loggers
   * @param enabled Whether all loggers should be enabled
   */
  static setGlobalEnabled(enabled: boolean): void {
    this.globalEnabled = enabled;
    this.loggers.forEach(logger => logger.setEnabled(enabled));
  }

  /**
   * Set the log level for all loggers
   * @param level The minimum log level to display
   */
  static setGlobalLevel(level: LogLevel): void {
    this.globalLevel = level;
    this.loggers.forEach(logger => logger.setLevel(level));
  }

  /**
   * Enable or disable a list of channels
   * @param channels An object mapping channel names to their enabled state
   */
  static configureChannels(channels: Record<string, boolean>): void {
    Object.entries(channels).forEach(([channel, enabled]) => {
      this.setChannelEnabled(channel, enabled);
    });
  }

  /**
   * Set the global timestamp format for all loggers
   * @param format The timestamp format to use
   */
  static setGlobalTimestampFormat(format: TimestampFormat): void {
    this.globalTimestampFormat = format;
    this.loggers.forEach(logger => logger.setTimestampFormat(format));
  }

  /**
   * Set whether to include channel names in log output globally
   * @param include Whether to include channel names
   */
  static setGlobalIncludeChannel(include: boolean): void {
    this.globalIncludeChannel = include;
    this.loggers.forEach(logger => logger.setIncludeChannel(include));
  }

  /**
   * Set the timestamp format for a specific channel
   * @param channel The channel to configure
   * @param format The timestamp format to use
   */
  static setChannelTimestampFormat(
    channel: string,
    format: TimestampFormat,
  ): void {
    if (this.loggers.has(channel)) {
      this.loggers.get(channel)!.setTimestampFormat(format);
    }
  }

  /**
   * Set whether to include the channel name for a specific channel
   * @param channel The channel to configure
   * @param include Whether to include the channel name
   */
  static setChannelIncludeChannel(channel: string, include: boolean): void {
    if (this.loggers.has(channel)) {
      this.loggers.get(channel)!.setIncludeChannel(include);
    }
  }

  /**
   * Get the pino logger instance
   * @returns The pino logger instance or null if not initialized
   */
  static getPinoLogger(): pino.Logger | null {
    if (!this.initialized) {
      this.initialize();
    }
    return this.pinoLogger;
  }
}
/**
 * Ergonomic alias for transaction timing (PLAT-11041/11042):
 *   Log.startTimer('clip_editor_usable_creator');
 *   Log.endTimer('clip_editor_usable_creator');
 */
export const Log = LogManager;
